import { useCallback, useMemo } from 'react';
import { useState } from 'react';

import useOutsideClick from '@cobalt/react-outside-click-hook';

import TransIcon from './TransIcon';
import WfLoading from '../wf-loading';
import { RefreshSvg } from '../wf-svg';

import './index.scss';

type OptionType = {
  label: string;
  value: string;
};

export enum SelectStatusEnum {
  NORMAL = 'NORMAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

export interface WfSelectProps {
  value: string;
  options: OptionType[];
  variation?: 'error';
  placeholder?: string;
  allowClear?: boolean;
  status?: SelectStatusEnum;
  t: (key: string) => string;
  onChange: (value: string, option?: OptionType) => void;
  onClear?: () => void;
  onRefresh?: () => void;
}

function WfSelect(props: WfSelectProps) {
  const {
    value: selectedValue,
    options,
    variation,
    placeholder,
    allowClear,
    status = SelectStatusEnum.NORMAL,
    t,
    onChange,
    onClear,
    onRefresh,
  } = props;

  const [open, setOpen] = useState(false);

  const showList = status === SelectStatusEnum.NORMAL;

  const showLoading = status === SelectStatusEnum.LOADING;

  const showError = status === SelectStatusEnum.ERROR;

  const selectInputClassName = useMemo(() => {
    const isError = variation === 'error';
    return `wf-select-input ${isError ? 'wf-select-input_error' : ''}`;
  }, [variation]);

  const label = useMemo(() => {
    const option = options.find((item) => item.value === selectedValue);
    return option?.label || '';
  }, [options, selectedValue]);

  const outsideClickHandler = useCallback(() => setOpen(false), []);
  const { onClickCapture } = useOutsideClick(outsideClickHandler);

  const handleChange = useCallback(
    (value: string) => {
      const option = options.find((item) => item.value === value);

      onChange(value, option);
      setOpen(false);
    },
    [onChange, options],
  );

  const handleClear = useCallback(() => {
    onClear?.();
    onChange('');
    setOpen(false);
  }, [onChange, onClear]);

  return (
    <div className="wf-select-container" onClick={onClickCapture}>
      <div className={selectInputClassName} onClick={() => setOpen((open) => !open)}>
        {selectedValue ? (
          <span className="wf-select-input-label" title={label}>
            {label}
          </span>
        ) : (
          <span className="wf-select-input-placeholder">{placeholder}</span>
        )}

        <TransIcon open={open} allowClear={allowClear} hasSelected={!!selectedValue} onClear={handleClear} />
      </div>

      {open && (
        <div className="wf-select-popup-container">
          <div className="wf-select-popup-wrap">
            {showList && (
              <ul className="wf-select-list">
                {options.map(({ label, value }) => (
                  <li
                    key={value}
                    className={`wf-select-option ${value === selectedValue ? 'wf-select-option_selected' : ''}`}
                    title={label}
                    onClick={() => handleChange(value)}
                  >
                    {label}
                  </li>
                ))}
              </ul>
            )}

            {showLoading && (
              <div className="wf-select-loading">
                <WfLoading />
              </div>
            )}

            {showError && (
              <div className="wf-select-error">
                <span className="wf-select-error-text">{t('webform.select.load.failed')}</span>
                <button className="wf-select-error-btn" onClick={onRefresh}>
                  <RefreshSvg className="wf-select-error-refresh-icon" />
                  {t('webform.REFRESH')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WfSelect;
